import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ExternalLink, MapPin, Building2, Check, Globe, Home, Rocket, ArrowRight } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import GalleryTab from "@/components/GalleryTab";
import DealerInstagramSettings from "@/components/DealerInstagramSettings";
import EmailSending from "@/components/EmailSending";
import {
  INPUT_CLS, Field, Grid2, HandleBlock, BioBlock, AboutUsBlock, HomeWidgetBlock, HomeTabsBlock, WebSocialBlock, PaymentHandlesBlock,
} from "@/components/profile/ProfileBlocks";

function Sub({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold">{title}</div>
      {children}
    </div>
  );
}

function SkipButton({ onSkip, skipped, testId }) {
  if (skipped) return null;
  return <Button variant="ghost" onClick={onSkip} className="w-full text-zinc-500" data-testid={testId}>Skip for now</Button>;
}

export function StepProfile({ form, bind, setForm, recId, slug }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <ImageUpload label="Profile photo" value={form.image_url} onChange={(url) => setForm((f) => ({ ...f, image_url: url }))}
          shape="circle" testId="setup-photo-upload" userRecordId={recId} kind="photo" />
      </div>
      <Grid2>
        <Field label="First name" testId="setup-first-name"><Input {...bind("first_name")} className={INPUT_CLS} /></Field>
        <Field label="Last name" testId="setup-last-name"><Input {...bind("last_name")} className={INPUT_CLS} /></Field>
      </Grid2>
      <Field label="Phone" testId="setup-phone"><Input inputMode="tel" {...bind("phone_no")} className={INPUT_CLS} /></Field>
      <Field label="Email"><Input value={form.email_id || ""} disabled className={`${INPUT_CLS} bg-zinc-50 text-zinc-500`} /></Field>
      <Sub title="Your dealer page">
        <HandleBlock form={form} setForm={setForm} slug={slug} recId={recId} />
      </Sub>
    </div>
  );
}

export function StepBusiness({ form, bind, setForm, recId }) {
  return (
    <div className="space-y-3">
      <Field label="Business name" icon={Building2} testId="setup-company-name"><Input placeholder="Your business or studio name" {...bind("company_name")} className={INPUT_CLS} /></Field>
      <Grid2>
        <Field label="City" icon={MapPin} testId="setup-city"><Input {...bind("city")} className={INPUT_CLS} /></Field>
        <Field label="State" testId="setup-state"><Input {...bind("state")} className={INPUT_CLS} /></Field>
      </Grid2>
      <BioBlock form={form} setForm={setForm} bind={bind} recId={recId} />
    </div>
  );
}

export function StepBrand({ form, setForm, recId, galleryCount }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <ImageUpload label="Company logo" value={form.dealer_logo} onChange={(url) => setForm((f) => ({ ...f, dealer_logo: url }))}
          shape="square" testId="setup-logo-upload" userRecordId={recId} kind="logo" />
        <div className="text-xs text-zinc-500">Your logo shows on your dealer page, flyers and emails.</div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] font-medium text-zinc-500">Gallery images</div>
          <span className={`text-[11px] font-semibold ${galleryCount >= 2 ? "text-emerald-600" : "text-zinc-400"}`} data-testid="setup-gallery-count">
            {galleryCount} uploaded · need 2
          </span>
        </div>
        <GalleryTab userRecordId={recId} />
      </div>
    </div>
  );
}

export function StepHome({ form, setForm, recId, slug }) {
  return (
    <div className="space-y-5">
      <Sub title="Home page widget">
        <HomeWidgetBlock form={form} />
      </Sub>
      <Sub title="About us">
        <AboutUsBlock form={form} setForm={setForm} slug={slug} recId={recId} />
      </Sub>
      {form.home_tab ? (
        <Sub title="Home page tabs">
          <HomeTabsBlock recId={recId} slug={slug} />
        </Sub>
      ) : (
        <p className="text-xs text-zinc-500" data-testid="home-tab-off-note">Want a Home tab on your dealer page? Turn it on under Settings → Dealer page.</p>
      )}
    </div>
  );
}

export function StepConnect({ bind, recId, onSkip, skipped }) {
  return (
    <div className="space-y-4">
      <WebSocialBlock bind={bind} />
      <DealerInstagramSettings userRecordId={recId} />
      <SkipButton onSkip={onSkip} skipped={skipped} testId="setup-connect-skip" />
    </div>
  );
}

export function StepMoney({ bind, recId, email, onSkip, skipped }) {
  return (
    <div className="space-y-5">
      <Sub title="Email sending">
        <EmailSending userRecordId={recId} defaultEmail={email} />
      </Sub>
      <Sub title="Payment handles (for invoices)">
        <PaymentHandlesBlock bind={bind} />
      </Sub>
      <SkipButton onSkip={onSkip} skipped={skipped} testId="setup-money-skip" />
    </div>
  );
}

export function StepLaunch({ form, slug, launched, onLaunch, onShare }) {
  const host = typeof window !== "undefined" ? window.location.host : "orevital.com";
  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-zinc-200 overflow-hidden" data-testid="setup-preview-card">
        <div className="h-20 bg-gradient-to-r from-violet-600 to-indigo-600 relative">
          {form.dealer_logo && <img src={form.dealer_logo} alt="logo" className="absolute right-4 top-4 h-12 w-12 rounded-xl bg-white p-1 object-contain" />}
          {form.image_url && <img src={form.image_url} alt="" className="absolute left-4 -bottom-7 h-16 w-16 rounded-full object-cover ring-4 ring-white" />}
        </div>
        <div className="px-4 pt-9 pb-4">
          <div className="font-bold text-zinc-900">{`${form.first_name || ""} ${form.last_name || ""}`.trim() || "Your name"}</div>
          <div className="text-sm text-zinc-600 flex items-center gap-1"><Building2 size={12} /> {form.company_name || "Your business"}</div>
          {(form.city || form.state) && <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {[form.city, form.state].filter(Boolean).join(", ")}</div>}
          {form.website_url && <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5"><Globe size={11} /> {form.website_url}</div>}
          {form.company && <p className="text-xs text-zinc-600 mt-2 leading-relaxed line-clamp-4">{form.company}</p>}
          <div className="text-[11px] text-zinc-400 mt-3 flex items-center gap-1">
            <Home size={11} /> {form.home_html ? "Home page widget set" : "No home page widget yet"} · {form.dealer_venmo || form.dealer_zelle ? "Payment handles set" : "No payment handles"}
          </div>
        </div>
      </div>
      {slug && (
        <a href={`/d/${slug}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline" data-testid="setup-view-page">
          View my page · {host}/d/{slug} <ExternalLink size={13} />
        </a>
      )}
      <button type="button" onClick={onLaunch} disabled={launched}
        className={`launch-btn relative w-full h-16 rounded-full overflow-hidden text-white font-black tracking-[0.25em] uppercase text-sm sm:text-base shadow-[0_12px_40px_-10px_rgba(124,58,237,.7)] transition-transform ${launched ? "opacity-90" : "hover:scale-[1.015] active:scale-[0.99]"}`}
        data-testid="setup-launch-btn">
        <span className="absolute inset-0 launch-shimmer" />
        <span className="relative z-10 inline-flex items-center justify-center gap-3">
          {launched ? <><Check size={18} strokeWidth={3} /> You're live</> : <><Rocket size={18} /> Launch my profile <ArrowRight size={18} /></>}
        </span>
      </button>
      {launched && onShare && (
        <Button variant="outline" onClick={onShare} className="w-full h-11 rounded-xl" data-testid="setup-share-card-btn">
          Get my "I'm live" share card
        </Button>
      )}
    </div>
  );
}
